import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useChat } from '../../context/ChatContext';
import { useDM } from '../../context/DMContext';
import { useAuth } from '../../hooks/useAuth';
import { useMessagePagination } from '../../hooks/useMessagePagination';
import { useInfiniteScroll } from '../../hooks/useInfiniteScroll';
import UserAvatar from './UserAvatar';

const ChatWindow = ({ chat, onMinimize, chatDimensions, setChatDimensions }) => {
  const {
    messages: globalMessages,
    onlineUsers,
    typingUsers: globalTypingUsers,
    loading: globalLoading,
    error: globalError,
    sendMessage: sendGlobalMessage,
    sendTypingIndicator: sendGlobalTypingIndicator,
    isAuthenticated
  } = useChat();

  const {
    messagesByConversation,
    sendDMMessage,
    setActiveConversation,
    subscribeToConversation,
    unsubscribeFromConversation,
    createOrGetConversation
  } = useDM();

  const { user } = useAuth();

  const [messageInput, setMessageInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [isResizing] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const chatRef = useRef(null);

  // Get current chat data based on chat type (global broadcast or 1:1 DM)
  const isGlobalChat = chat?.type === 'global';


  // Use pagination for message loading
  const {
    messages: paginatedMessages,
    loading: paginationLoading,
    hasMore,
    error: paginationError,
    loadInitialMessages,
    loadOlderMessages,
    addMessage,
    resetPagination
  } = useMessagePagination(isGlobalChat);

  // Set up infinite scroll
  const sentinelRef = useInfiniteScroll(loadOlderMessages, hasMore, paginationLoading);

  // For DM chats, always use context messages; for global chat, prefer paginated
  const rawMessages = useMemo(() => {
    if (isGlobalChat) {
      return paginatedMessages.length > 0 ? paginatedMessages : globalMessages;
    } else {
      return messagesByConversation[chat?.conversationId] || [];
    }
  }, [
    isGlobalChat,
    paginatedMessages,
    globalMessages,
    messagesByConversation,
    chat?.conversationId
  ]);

  // Deduplicate messages by ID to prevent React key warnings
  const currentMessages = useMemo(() => {
    return rawMessages.reduce((unique, message) => {
      const existingIndex = unique.findIndex(m => m.id === message.id);
      if (existingIndex === -1) {
        unique.push(message);
      }
      return unique;
    }, []);
  }, [rawMessages]);

  const currentLoading = paginationLoading || globalLoading;
  const currentError = paginationError || globalError;
  const currentTypingUsers = isGlobalChat ? globalTypingUsers : [];

  // Auto-scroll to bottom when new messages arrive
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Track previous message count to only scroll on new messages
  const prevMessageCountRef = useRef(0);

  useEffect(() => {
    const currentCount = currentMessages.length;
    const prevCount = prevMessageCountRef.current;

    // Only scroll if we have new messages (count increased) and we had messages before
    // Don't scroll on initial load or when switching between chats
    if (currentCount > prevCount && prevCount > 0) {
      scrollToBottom();
    }

    prevMessageCountRef.current = currentCount;
  }, [currentMessages]);

  // Focus input when chat is active
  useEffect(() => {
    if (chat && inputRef.current) {
      inputRef.current.focus();
    }
  }, [chat]);

  // Setup DM conversation subscription
  useEffect(() => {
    if (!isGlobalChat && chat?.conversationId && !chat?.isTemporary) {
      setActiveConversation(chat.conversationId);
      subscribeToConversation(chat.conversationId);

      return () => {
        unsubscribeFromConversation(chat.conversationId);
      };
    }
  }, [chat?.conversationId, chat?.isTemporary, isGlobalChat, setActiveConversation, subscribeToConversation, unsubscribeFromConversation]);

  // Load messages when chat changes (pagination)
  useEffect(() => {
    if (chat) {
      resetPagination();
      // Reset message count when switching chats to prevent unwanted scrolling
      prevMessageCountRef.current = 0;
      if (isGlobalChat) {
        loadInitialMessages();
      } else if (chat.conversationId && !chat.isTemporary) {
        loadInitialMessages(chat.conversationId);
      }
      // For temporary chats, don't load messages as there are none yet
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally keyed on chat identity fields only; depending on the whole `chat` object would reload messages on every re-render
  }, [chat?.conversationId, chat?.type, chat?.isTemporary, isGlobalChat, loadInitialMessages, resetPagination]);

  // Initial scroll to bottom for regular chats when messages load
  const [hasInitiallyScrolled, setHasInitiallyScrolled] = useState(false);

  useEffect(() => {
    // Reset scroll flag when chat changes
    setHasInitiallyScrolled(false);
    // Reset last processed message when switching chats
    lastProcessedMessageRef.current = null;
  }, [chat?.id, chat?.conversationId, chat?.type]);

  useEffect(() => {
    if (currentMessages.length > 0 && !hasInitiallyScrolled && !currentLoading) {
      // Use setTimeout to ensure DOM is updated
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'auto' });
        setHasInitiallyScrolled(true);
      }, 100);
    }
  }, [currentMessages.length, hasInitiallyScrolled, currentLoading]);

  // Track last processed message to prevent infinite loops
  const lastProcessedMessageRef = useRef(null);

  // Handle real-time message updates with proper memoization to prevent loops
  const latestGlobalMessage = useMemo(() => {
    return globalMessages.length > 0 ? globalMessages[globalMessages.length - 1] : null;
  }, [globalMessages]);

  const latestDMMessage = useMemo(() => {
    const dmMessages = messagesByConversation[chat?.conversationId] || [];
    return dmMessages.length > 0 ? dmMessages[dmMessages.length - 1] : null;
  }, [messagesByConversation, chat?.conversationId]);

  useEffect(() => {
    if (isGlobalChat && latestGlobalMessage) {
      // If there's a new message that we haven't processed yet
      if (latestGlobalMessage.id !== lastProcessedMessageRef.current) {
        // Check if this message is already in paginated messages
        const isAlreadyInPaginated = paginatedMessages.some(msg => msg.id === latestGlobalMessage.id);

        if (!isAlreadyInPaginated) {
          addMessage(latestGlobalMessage);
        }
        lastProcessedMessageRef.current = latestGlobalMessage.id;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- paginatedMessages is intentionally omitted: this effect only reacts to new incoming messages, and addMessage mutates paginatedMessages which would re-trigger it
  }, [isGlobalChat, latestGlobalMessage, addMessage]);

  useEffect(() => {
    if (!isGlobalChat && latestDMMessage) {
      // If there's a new message that we haven't processed yet
      if (latestDMMessage.id !== lastProcessedMessageRef.current) {
        // Check if this message is already in paginated messages
        const isAlreadyInPaginated = paginatedMessages.some(msg => msg.id === latestDMMessage.id);

        if (!isAlreadyInPaginated) {
          addMessage(latestDMMessage);
        }
        lastProcessedMessageRef.current = latestDMMessage.id;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- paginatedMessages is intentionally omitted: this effect only reacts to new incoming messages, and addMessage mutates paginatedMessages which would re-trigger it
  }, [isGlobalChat, latestDMMessage, addMessage]);

  // Handle window resize for mobile detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Handle resize functionality
  // Handle typing indicators
  const handleInputChange = (e) => {
    setMessageInput(e.target.value);

    if (!isTyping && e.target.value.trim()) {
      setIsTyping(true);
      if (isGlobalChat) {
        sendGlobalTypingIndicator();
      }
    }

    // Clear existing timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    // Stop typing indicator after 3 seconds of inactivity
    typingTimeoutRef.current = setTimeout(() => {
      setIsTyping(false);
    }, 3000);
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();

    if (!messageInput.trim()) return;

    const message = messageInput.trim();
    setMessageInput('');
    setIsTyping(false);

    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    try {
      if (isGlobalChat) {
        await sendGlobalMessage(message);
      } else if (chat?.type === 'dm') {
        // For DM chats
        if (chat.conversationId) {
          // Existing conversation
          await sendDMMessage(chat.conversationId, message);
        } else if (chat.otherUserId) {
          // Temporary chat - create conversation first
          const conversationId = await createOrGetConversation(chat.otherUserId);
          if (conversationId) {
            // Update the chat with the real conversation ID
            if (window.updateChatConversationId) {
              window.updateChatConversationId(chat.id, conversationId);
            }
            // Send the message
            await sendDMMessage(conversationId, message);
          }
        }
      }
    } catch (error) {
      // Error is already logged and handled in the respective functions
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage(e);
    }
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;

    if (diff < 60000) return 'now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h`;
    return date.toLocaleDateString();
  };

  // Handle navigation to player profile
  const handlePlayerClick = useCallback((user) => {
    if (!user) return;

    // Navigate to player profile
    window.location.href = `/profile/${user.id}`;
  }, []);

  if (!isAuthenticated || !chat) {
    return null;
  }

  return (
    <div
      ref={chatRef}
      className={`backdrop-blur-sm bg-white/80 dark:bg-zinc-800/80 flex flex-col relative flex-1 ${isResizing ? 'select-none' : ''} ${
        isMobile
          ? 'min-h-0'
          : 'min-h-0'
      }`}
      style={{
        height: chatDimensions?.height || 'auto',
        width: chatDimensions?.width || 'auto'
      }}
    >
      {/* Top section with online indicator */}
      <div className="relative">
        {/* Online Users Indicator for Global Chat */}
        {isGlobalChat && onlineUsers.length > 0 && (
          <div className="text-center py-1">
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {onlineUsers.length} user{onlineUsers.length !== 1 ? 's' : ''} online
            </span>
          </div>
        )}
      </div>


      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto p-3 space-y-3">
        {currentLoading && currentMessages.length === 0 && (
          <div className="text-center text-gray-500 dark:text-gray-400 py-4">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#31C590] mx-auto"></div>
            <p className="mt-2 text-sm">Loading messages...</p>
          </div>
        )}

        {currentError && (
          <div className="text-red-500 dark:text-red-400 text-sm text-center py-2">
            Failed to load messages. Please try again.
          </div>
        )}

        {/* Pagination: Load More Messages Button */}
        {hasMore && currentMessages.length > 0 && (
          <div ref={sentinelRef} className="text-center py-2">
            {paginationLoading ? (
              <div className="flex items-center justify-center gap-2 text-gray-500 dark:text-gray-400">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#31C590]"></div>
                <span className="text-sm">Loading older messages...</span>
              </div>
            ) : (
              <button
                onClick={loadOlderMessages}
                className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 text-sm transition-colors"
              >
                Load older messages
              </button>
            )}
          </div>
        )}

        {currentMessages.length === 0 && !currentLoading && (
          <div className="text-center text-gray-500 dark:text-gray-400 py-8">
            <svg className="w-12 h-12 mx-auto mb-2 text-gray-300 dark:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
            <p className="text-sm">
              {isGlobalChat ? 'No messages yet.' : 'No messages in this conversation.'}
            </p>
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
              {isGlobalChat ? 'Be the first to say hello!' : 'Start the conversation!'}
            </p>
          </div>
        )}

        {/* Messages */}
        {currentMessages.map((message) => {
          const isMyMessage = message.user_id === user?.id;

          return (
            <div key={message.id} className={`flex gap-2 ${isMyMessage ? 'flex-row-reverse' : ''}`}>
              {/* Avatar */}
              <UserAvatar
                user={{
                  id: message.user_id,
                  avatar_url: message.profiles?.avatar_url || message.users?.avatar_url,
                  username: message.profiles?.display_name || message.users?.display_name || message.profiles?.username || message.users?.username
                }}
                size="w-8 h-8 flex-shrink-0"
                textSize="text-sm"
              />

              {/* Message Content */}
              <div className={`flex flex-col max-w-[70%] ${isMyMessage ? 'items-end' : 'items-start'}`}>
                {/* Username and time */}
                <div className={`flex items-center gap-2 mb-1 ${isMyMessage ? 'flex-row-reverse' : ''}`}>
                  {isGlobalChat ? (
                    <button
                      onClick={() => handlePlayerClick({
                        id: message.user_id,
                        username: message.profiles?.display_name || message.users?.display_name || message.profiles?.username || message.users?.username,
                        display_name: message.profiles?.display_name || message.users?.display_name,
                        avatar_url: message.profiles?.avatar_url || message.users?.avatar_url
                      })}
                      className="text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 transition-colors"
                      title="View player profile"
                    >
                      {message.profiles?.display_name || message.users?.display_name || message.profiles?.username || message.users?.username || 'Unknown User'}
                    </button>
                  ) : (
                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      {message.profiles?.display_name || message.users?.display_name || message.profiles?.username || message.users?.username || 'Unknown User'}
                    </span>
                  )}
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {formatTime(message.created_at)}
                  </span>
                </div>

                {/* Message bubble */}
                <div className={`rounded-lg p-3 text-sm ${
                  isMyMessage
                    ? 'text-white'
                    : 'bg-gray-100 dark:bg-zinc-700 text-gray-900 dark:text-gray-100'
                }`}
                style={isMyMessage ? { backgroundColor: '#38c494' } : {}}>
                  {message.message}
                </div>
              </div>
            </div>
          );
        })}

        {/* Typing indicators */}
        {currentTypingUsers.length > 0 && (
          <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
            <div className="flex space-x-1">
              <div className="w-1 h-1 bg-gray-400 dark:bg-gray-500 rounded-full animate-bounce"></div>
              <div className="w-1 h-1 bg-gray-400 dark:bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
              <div className="w-1 h-1 bg-gray-400 dark:bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
            </div>
            <span>
              {currentTypingUsers.length === 1 ? 'Someone is typing...' : `${currentTypingUsers.length} people are typing...`}
            </span>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Message Input */}
      <div className="border-t border-zinc-700 p-3">
        <form onSubmit={handleSendMessage} className="flex gap-2 items-end">
          <textarea
            ref={inputRef}
            value={messageInput}
            onChange={handleInputChange}
            onKeyPress={handleKeyPress}
            placeholder={isGlobalChat ? "Type a message..." : `Message ${chat.title}...`}
            className="flex-1 border border-gray-300 dark:border-zinc-600 bg-white dark:bg-zinc-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 rounded-lg px-3 py-2 text-base focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent resize-none overflow-y-auto"
            maxLength={1000}
            rows={1}
            style={{
              minHeight: '38px',
              maxHeight: '120px'
            }}
            onInput={(e) => {
              // Auto-resize textarea
              e.target.style.height = 'auto';
              e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
            }}
          />
          <button
            type="submit"
            disabled={!messageInput.trim()}
            className="bg-blue-600 hover:bg-blue-700 dark:bg-blue-600 dark:hover:bg-blue-700 disabled:bg-gray-300 dark:disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg px-4 py-2 text-sm font-medium transition-colors flex-shrink-0"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
            </svg>
          </button>
        </form>
      </div>
    </div>
  );
};

export default ChatWindow;
